import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SetPinDialog from "./SetPinDialog";
import { pickerWillAutoOpen } from "components/DefaultSystemPicker";
import { dismissQuickSignInOffer, shouldOfferQuickSignIn } from "utils/quickSignIn";

// After a full sign-in (password or code) on a device with no PIN, offer one, once.
// "Not now" is remembered for this device; a PIN can still be set later under
// Sign-in & security. Waits while the first-login system picker is open: that is a
// blocking dialog, and the offer would sit under it, unclickable.
function QuickSignInOffer() {
  const authToken = useSelector((state) => state.auth.authToken);
  const userDetails = useSelector((state) => state.auth.user?.userDetails);
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const onAuthPage = /^\/(login|set-password|forgot-password)(\/|$)/.test(pathname || "");

  const pickerFirst = pickerWillAutoOpen(userDetails);

  useEffect(() => {
    setVisible(!!authToken && !onAuthPage && !pickerFirst && shouldOfferQuickSignIn());
  }, [authToken, onAuthPage, pickerFirst]);

  if (!visible) return null;

  return (
    <>
      <Card
        role="dialog"
        aria-label="Set up quick sign-in"
        sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 1300, maxWidth: 360, p: 2 }}
      >
        <SoftTypography variant="button" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
          Sign in faster on this device?
        </SoftTypography>
        <SoftTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
          Set a 4 to 6 digit PIN and use it instead of your password next time, on this device only.
        </SoftTypography>
        <SoftBox display="flex" justifyContent="flex-end" gap={1}>
          <SoftButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={() => {
              dismissQuickSignInOffer(true);
              setVisible(false);
            }}
          >
            Not now
          </SoftButton>
          <SoftButton variant="gradient" color="info" size="small" onClick={() => setDialogOpen(true)}>
            Set a PIN
          </SoftButton>
        </SoftBox>
      </Card>
      <SetPinDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          dismissQuickSignInOffer(false);
          setVisible(false);
        }}
      />
    </>
  );
}

export default QuickSignInOffer;
